import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain,
  CheckCircle2,
  AlertCircle,
  Zap,
  DollarSign,
  Clock,
  Key,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  Activity,
  BarChart3,
  Trash2,
  Cpu,
  Layers,
  MessageSquare,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { api } from '@/services/client';
import { toast } from 'sonner';

interface AIProvider {
  id: string;
  name: string;
  models: string[];
  available: boolean;
  configured: boolean;
  apiKeySet: boolean;
  endpoint?: string;
  enabled?: boolean;
  priority?: number;
  healthError?: string;
  latency?: number;
  costPerRequest?: number;
  requestsToday?: number;
}

interface AIModel {
  id: string;
  provider: string;
  name: string;
  type: 'chat' | 'completion' | 'embedding';
  costPer1kTokens: number;
  maxTokens: number;
  enabled: boolean;
}

interface ProviderConfig {
  apiKey: string;
  endpoint?: string;
  enabled: boolean;
  priority: number;
}

interface HealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy';
  providers: number;
  latency_ms: number;
}

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Google Gemini',
  google: 'Google Gemini',
  cohere: 'Cohere',
};

const PROVIDER_LOGOS: Record<string, string> = {
  openai: '🤖',
  anthropic: '🧠',
  gemini: '🔮',
  google: '🔮',
  cohere: '⚡',
};

const MODEL_TYPE_META: Record<AIModel['type'], { label: string; icon: typeof Cpu }> = {
  chat: { label: 'Chat', icon: MessageSquare },
  completion: { label: 'Completion', icon: Cpu },
  embedding: { label: 'Embedding', icon: Layers },
};

/** Format an integer (ms / count) for display: 0 → "0", undefined → "—". */
function fmtInt(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString() : '—';
}

/** Format a USD amount: 0 → "$0.00", undefined → "—". */
function fmtUsd(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
}

/**
 * Format a per-1k-token price with appropriate precision.
 * Most LLM input prices are < $0.01/1k, so toFixed(2) would round
 * them all to "$0.00". Use up to 5 fractional digits, trimming
 * trailing zeros so we don't show "$0.00250".
 */
function fmtPricePer1k(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  if (n === 0) return '$0';
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(4)}`;
  // Tiny values: use enough precision, then strip trailing zeros.
  return `$${n.toFixed(5).replace(/0+$/, '').replace(/\.$/, '')}`;
}

export const AIProviderSettingsPage = () => {
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [models, setModels] = useState<AIModel[]>([]);
  const [configs, setConfigs] = useState<Record<string, ProviderConfig>>({});
  const [showApiKeys, setShowApiKeys] = useState<Record<string, boolean>>({});
  const [showAllModels, setShowAllModels] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [testing, setTesting] = useState<Record<string, boolean>>({});
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = async (withSpinner = false) => {
    if (withSpinner) setLoading(true);
    setError(null);
    const results = await Promise.allSettled([
      fetchProviders(),
      fetchModels(),
      fetchHealthStatus(),
    ]);
    const failed = results.filter(r => r.status === 'rejected');
    if (failed.length > 0) {
      setError(`Some data failed to load (${failed.length}/${results.length})`);
    }
    if (withSpinner) setLoading(false);
  };

  useEffect(() => {
    fetchAll(true);
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await api.get<{ providers: AIProvider[] }>('/llm/providers');
      const fetchedProviders = response.data?.providers ?? [];
      setProviders(fetchedProviders);

      // Prefill non-secret settings. The server never returns the API key itself;
      // `apiKeySet` drives the masked placeholder instead.
      setConfigs(prev => {
        const next = { ...prev };
        fetchedProviders.forEach(p => {
          if (!next[p.id] && p.endpoint) {
            next[p.id] = {
              apiKey: '',
              endpoint: p.endpoint,
              enabled: p.enabled ?? true,
              priority: p.priority ?? 1,
            };
          }
        });
        return next;
      });
    } catch (err) {
      console.error('Failed to fetch providers:', err);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await api.get<{ models: AIModel[] }>('/llm/models');
      setModels(response.data?.models ?? []);
    } catch (err) {
      console.error('Failed to fetch models:', err);
    }
  };

  const fetchHealthStatus = async () => {
    try {
      const response = await api.get<HealthStatus>('/llm/health');
      setHealthStatus(response.data ?? null);
    } catch (err) {
      console.error('Failed to fetch health status:', err);
    }
  };

  const saveProviderConfig = async (providerId: string) => {
    const config = configs[providerId];
    if (!config) return;

    setSaving(prev => ({ ...prev, [providerId]: true }));
    try {
      await api.put(`/llm/providers/${encodeURIComponent(providerId)}`, {
        api_key: config.apiKey,
        endpoint: config.endpoint,
        enabled: config.enabled,
        priority: config.priority,
      });
      setProviders(prev => prev.map(p =>
        p.id === providerId
          ? { ...p, configured: true, apiKeySet: !!config.apiKey }
          : p
      ));
      toast.success(`${PROVIDER_LABELS[providerId] ?? providerId} configuration saved`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to save configuration');
    } finally {
      setSaving(prev => ({ ...prev, [providerId]: false }));
    }
  };

  const testProvider = async (providerId: string) => {
    const config = configs[providerId];
    if (!config) return;

    setTesting(prev => ({ ...prev, [providerId]: true }));
    try {
      const response = await api.post<{ status: string; latency_ms: number; error?: string }>(
        `/llm/providers/${encodeURIComponent(providerId)}/test`,
        { api_key: config.apiKey, endpoint: config.endpoint }
      );
      const result = response.data;
      const label = PROVIDER_LABELS[providerId] ?? providerId;
      if (result?.status === 'healthy') {
        toast.success(`${label} is healthy (${result.latency_ms}ms)`);
        setProviders(prev => prev.map(p =>
          p.id === providerId
            ? { ...p, available: true, latency: result.latency_ms }
            : p
        ));
      } else {
        toast.warning(`${label} is unhealthy: ${result?.error || 'connection failed'}`);
        setProviders(prev => prev.map(p =>
          p.id === providerId ? { ...p, available: false } : p
        ));
      }
      await fetchHealthStatus();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Connection test failed');
    } finally {
      setTesting(prev => ({ ...prev, [providerId]: false }));
    }
  };

  const clearProviderConfig = (providerId: string) => {
    const label = PROVIDER_LABELS[providerId] ?? providerId;
    if (!window.confirm(`Clear API key for ${label}? You'll need to re-enter it to use this provider.`)) {
      return;
    }
    setConfigs(prev => {
      const next = { ...prev };
      delete next[providerId];
      return next;
    });
    setProviders(prev => prev.map(p =>
      p.id === providerId
        ? { ...p, configured: false, apiKeySet: false, available: false }
        : p
    ));
    toast.info(`${label} configuration cleared locally — save to persist`);
  };

  const toggleApiKeyVisibility = (providerId: string) => {
    setShowApiKeys(prev => ({ ...prev, [providerId]: !prev[providerId] }));
  };

  const updateConfig = (providerId: string, updates: Partial<ProviderConfig>) => {
    setConfigs(prev => ({
      ...prev,
      [providerId]: {
        ...(prev[providerId] ?? { apiKey: '', enabled: true, priority: 1 }),
        ...updates,
      },
    }));
  };

  // Derived stats — recomputed from real data, no hidden placeholders.
  const stats = useMemo(() => {
    const configuredCount = providers.filter(p => p.configured).length;
    const healthyCount = providers.filter(p => p.available && p.configured).length;
    const totalRequests = providers.reduce((sum, p) => sum + (p.requestsToday ?? 0), 0);
    const totalCost = providers.reduce(
      (sum, p) => sum + (p.requestsToday ?? 0) * (p.costPerRequest ?? 0),
      0
    );
    const measuredLatencies = providers
      .map(p => p.latency)
      .filter((n): n is number => typeof n === 'number' && n > 0);
    const avgLatency = measuredLatencies.length
      ? Math.round(measuredLatencies.reduce((a, b) => a + b, 0) / measuredLatencies.length)
      : healthStatus?.latency_ms ?? null;
    return { configuredCount, healthyCount, totalRequests, totalCost, avgLatency };
  }, [providers, healthStatus]);

  const sortedModels = useMemo(
    () => [...models].sort((a, b) => a.costPer1kTokens - b.costPer1kTokens),
    [models]
  );
  const visibleModels = showAllModels ? sortedModels : sortedModels.slice(0, 5);
  const healthLabel = healthStatus?.status
    ? healthStatus.status.charAt(0).toUpperCase() + healthStatus.status.slice(1)
    : '—';
  const healthTone = healthStatus?.status === 'healthy'
    ? 'success'
    : healthStatus?.status === 'degraded'
    ? 'warning'
    : 'error';

  return (
    <PageLayout title="AI Provider Settings" description="Configure your AI provider integrations" icon={Brain}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-8 bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl border border-khiimori-border/10"
        >
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => fetchAll(true)}
              disabled={loading}
              className="px-4 py-2 bg-primary-500/20 text-primary-400 rounded-lg hover:bg-primary-500/30 transition-colors flex items-center gap-2 disabled:opacity-50 border border-primary-500/30"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing…' : 'Refresh All'}
            </button>
            {error && (
              <span className="text-sm text-error-400">{error}</span>
            )}
          </div>

          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-blue-400" />
                <span className="text-blue-400 text-sm font-medium">Active Providers</span>
              </div>
              <div className="text-3xl font-bold text-khiimori-text-primary">
                {fmtInt(stats.healthyCount)}
              </div>
              <div className="text-xs text-khiimori-text-muted mt-1">
                of {fmtInt(providers.length)} configured
              </div>
            </div>

            <div className="p-4 bg-success-500/10 rounded-xl border border-success-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-5 h-5 text-success-400" />
                <span className="text-success-400 text-sm font-medium">Requests Today</span>
              </div>
              <div className="text-3xl font-bold text-khiimori-text-primary">
                {fmtInt(stats.totalRequests)}
              </div>
              <div className="text-xs text-khiimori-text-muted mt-1">across all providers</div>
            </div>

            <div className="p-4 bg-purple-500/10 rounded-xl border border-purple-500/20">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-purple-400" />
                <span className="text-purple-400 text-sm font-medium">Cost Today</span>
              </div>
              <div className="text-3xl font-bold text-khiimori-text-primary">
                {fmtUsd(stats.totalCost)}
              </div>
              <div className="text-xs text-khiimori-text-muted mt-1">estimated spend</div>
            </div>

            <div className="p-4 bg-warning-500/10 rounded-xl border border-warning-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-warning-400" />
                <span className="text-warning-400 text-sm font-medium">Avg Latency</span>
              </div>
              <div className="text-3xl font-bold text-khiimori-text-primary">
                {stats.avgLatency != null ? `${stats.avgLatency}ms` : '—'}
              </div>
              <div className="text-xs text-khiimori-text-muted mt-1">response time</div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Providers List */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl p-6 border border-khiimori-border/10">
              <h2 className="text-xl font-bold text-khiimori-text-primary mb-6">AI Providers</h2>

              <div className="space-y-4">
                <AnimatePresence mode="popLayout">
                  {providers.map((provider, index) => {
                    const logo = PROVIDER_LOGOS[provider.id] ?? '🤖';
                    const providerCost = (provider.requestsToday ?? 0) * (provider.costPerRequest ?? 0);
                    return (
                      <motion.div
                        key={provider.id || index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ delay: index * 0.05 }}
                        className="border border-khiimori-border/10 rounded-xl p-6 hover:border-khiimori-border/20 transition-all bg-khiimori-bg-elevated/5 backdrop-blur-xl"
                      >
                        <div className="flex items-start gap-4">
                          {/* Provider Icon */}
                          <div className="text-4xl flex-shrink-0">{logo}</div>

                          {/* Provider Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-lg font-bold text-khiimori-text-primary">
                                {PROVIDER_LABELS[provider.id] ?? provider.name}
                              </h3>
                              {provider.available && provider.configured && (
                                <CheckCircle2 className="w-5 h-5 text-success-400" aria-label="Healthy" />
                              )}
                              {!provider.configured && (
                                <AlertCircle className="w-5 h-5 text-warning-400" aria-label="Not configured" />
                              )}
                            </div>

                            {/* Status Badges */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              <span title={provider.healthError} className={`px-2 py-1 text-xs rounded-full ${
                                provider.available
                                  ? 'bg-success-500/20 text-success-400 border border-success-500/30'
                                  : 'bg-error-500/20 text-error-400 border border-error-500/30'
                              }`}>
                                {provider.available ? 'Available' : 'Unavailable'}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                provider.configured
                                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                                  : 'bg-khiimori-bg-elevated/10 text-khiimori-text-muted border border-khiimori-border/20'
                              }`}>
                                {provider.configured ? 'Configured' : 'Not Configured'}
                              </span>
                              <span className="px-2 py-1 text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-full">
                                {fmtInt(provider.models?.length)} models
                              </span>
                            </div>

                            {/* Configuration Form */}
                            <div className="space-y-3">
                              <div>
                                <label className="text-sm font-medium text-khiimori-text-secondary mb-1 block">
                                  API Key
                                </label>
                                <div className="flex gap-2">
                                  <div className="flex-1 relative">
                                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-khiimori-text-muted" />
                                    <input
                                      type={showApiKeys[provider.id] ? 'text' : 'password'}
                                      value={configs[provider.id]?.apiKey ?? ''}
                                      onChange={(e) => updateConfig(provider.id, { apiKey: e.target.value })}
                                      placeholder={provider.apiKeySet ? '••••••••' : 'Enter API key...'}
                                      autoComplete="off"
                                      spellCheck={false}
                                      className="w-full pl-10 pr-4 py-2 border border-khiimori-border/20 rounded-lg focus:border-primary-500/50 focus:outline-none text-sm bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => toggleApiKeyVisibility(provider.id)}
                                    aria-label={showApiKeys[provider.id] ? 'Hide API key' : 'Show API key'}
                                    className="px-3 py-2 bg-khiimori-bg-elevated/10 rounded-lg hover:bg-khiimori-bg-elevated/20 transition-colors border border-khiimori-border/20"
                                  >
                                    {showApiKeys[provider.id] ? (
                                      <EyeOff className="w-4 h-4 text-khiimori-text-muted" />
                                    ) : (
                                      <Eye className="w-4 h-4 text-khiimori-text-muted" />
                                    )}
                                  </button>
                                </div>
                              </div>

                              <div className="flex items-center justify-between flex-wrap gap-3">
                                <div className="flex items-center gap-4">
                                  <label className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={configs[provider.id]?.enabled ?? true}
                                      onChange={(e) => updateConfig(provider.id, { enabled: e.target.checked })}
                                      className="w-4 h-4 text-primary-500 rounded border-khiimori-border bg-khiimori-bg-secondary"
                                    />
                                    <span className="text-sm text-khiimori-text-secondary">Enabled</span>
                                  </label>

                                  <div>
                                    <label className="text-xs text-khiimori-text-muted mr-2">Priority:</label>
                                    <select
                                      value={configs[provider.id]?.priority ?? 1}
                                      onChange={(e) => updateConfig(provider.id, { priority: Number(e.target.value) })}
                                      className="px-2 py-1 text-sm border border-khiimori-border/20 rounded bg-khiimori-bg-elevated/10 text-khiimori-text-primary"
                                    >
                                      <option value={1}>High</option>
                                      <option value={2}>Medium</option>
                                      <option value={3}>Low</option>
                                    </select>
                                  </div>
                                </div>

                                <div className="flex gap-2">
                                  {provider.configured && (
                                    <button
                                      type="button"
                                      onClick={() => clearProviderConfig(provider.id)}
                                      className="px-3 py-1.5 text-sm bg-khiimori-bg-elevated/10 text-khiimori-text-muted rounded-lg hover:bg-error-500/20 hover:text-error-400 transition-colors flex items-center gap-1 border border-khiimori-border/20"
                                      title="Clear saved configuration"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                      Clear
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => testProvider(provider.id)}
                                    disabled={!configs[provider.id]?.apiKey || testing[provider.id]}
                                    className="px-3 py-1.5 text-sm bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 border border-blue-500/30"
                                  >
                                    <Zap className={`w-3 h-3 ${testing[provider.id] ? 'animate-pulse' : ''}`} />
                                    {testing[provider.id] ? 'Testing…' : 'Test'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => saveProviderConfig(provider.id)}
                                    disabled={!configs[provider.id]?.apiKey || saving[provider.id]}
                                    className="px-3 py-1.5 text-sm bg-primary-500 text-khiimori-text-primary rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                  >
                                    <Save className="w-3 h-3" />
                                    {saving[provider.id] ? 'Saving…' : 'Save'}
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Provider Stats — only when configured (matches original layout) */}
                            {provider.available && provider.configured && (
                              <div className="mt-4 pt-4 border-t border-khiimori-border/10">
                                <div className="grid grid-cols-3 gap-4 text-center">
                                  <div>
                                    <div className="text-xs text-khiimori-text-muted">Requests</div>
                                    <div className="text-lg font-bold text-khiimori-text-primary">
                                      {fmtInt(provider.requestsToday)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-khiimori-text-muted">Latency</div>
                                    <div className="text-lg font-bold text-khiimori-text-primary">
                                      {provider.latency != null ? `${provider.latency}ms` : '—'}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-khiimori-text-muted">Cost</div>
                                    <div className="text-lg font-bold text-khiimori-text-primary">
                                      {fmtUsd(providerCost)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                {providers.length === 0 && !loading && (
                  <div className="text-center py-12 text-khiimori-text-muted">
                    No providers available.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Health Status */}
            <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl p-6 border border-khiimori-border/10">
              <h3 className="text-lg font-bold text-khiimori-text-primary mb-4 flex items-center gap-2">
                <Activity className={`w-5 h-5 ${
                  healthTone === 'success' ? 'text-success-400'
                  : healthTone === 'warning' ? 'text-warning-400'
                  : 'text-error-400'
                }`} />
                System Health
              </h3>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-3 rounded-lg border ${
                  healthTone === 'success' ? 'bg-success-500/10 border-success-500/20'
                  : healthTone === 'warning' ? 'bg-warning-500/10 border-warning-500/20'
                  : 'bg-error-500/10 border-error-500/20'
                }`}>
                  <span className={`text-sm ${
                    healthTone === 'success' ? 'text-success-300'
                    : healthTone === 'warning' ? 'text-warning-300'
                    : 'text-error-300'
                  }`}>Status</span>
                  <span className={`font-bold ${
                    healthTone === 'success' ? 'text-success-400'
                    : healthTone === 'warning' ? 'text-warning-400'
                    : 'text-error-400'
                  }`}>{healthLabel}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <span className="text-sm text-blue-300">Healthy Providers</span>
                  <span className="font-bold text-blue-400">
                    {fmtInt(healthStatus?.providers)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                  <span className="text-sm text-purple-300">Avg Latency</span>
                  <span className="font-bold text-purple-400">
                    {healthStatus?.latency_ms != null ? `${healthStatus.latency_ms}ms` : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Models Summary */}
            <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl p-6 border border-khiimori-border/10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-khiimori-text-primary flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary-400" />
                  Available Models
                </h3>
                <span className="text-xs text-khiimori-text-muted">{fmtInt(models.length)}</span>
              </div>
              <div className="space-y-2">
                {visibleModels.map((model) => {
                  const meta = MODEL_TYPE_META[model.type] ?? MODEL_TYPE_META.chat;
                  const Icon = meta.icon;
                  const providerLabel = PROVIDER_LABELS[model.provider] ?? model.provider;
                  return (
                    <div
                      key={`${model.provider}-${model.id}`}
                      className="p-3 bg-khiimori-bg-elevated/10 rounded-lg border border-khiimori-border/10"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium text-khiimori-text-primary text-sm truncate">
                          {model.name}
                        </div>
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-khiimori-bg-elevated/20 text-khiimori-text-muted border border-khiimori-border/10 shrink-0">
                          <Icon className="w-2.5 h-2.5" />
                          {meta.label}
                        </span>
                      </div>
                      <div className="text-xs text-khiimori-text-muted mt-1 flex items-center justify-between">
                        <span>{providerLabel}</span>
                        <span>{fmtPricePer1k(model.costPer1kTokens)}/1k tokens</span>
                      </div>
                    </div>
                  );
                })}
                {models.length === 0 && !loading && (
                  <div className="text-center text-sm text-khiimori-text-muted py-4">
                    No models available.
                  </div>
                )}
                {models.length > 5 && (
                  <button
                    type="button"
                    onClick={() => setShowAllModels(v => !v)}
                    className="w-full text-center text-sm text-primary-400 hover:text-primary-300 pt-2"
                  >
                    {showAllModels
                      ? 'Show fewer'
                      : `+${models.length - 5} more models`}
                  </button>
                )}
              </div>
            </div>

            {/* Tips */}
            <div className="bg-gradient-primary rounded-2xl p-6 border border-primary-500/20">
              <h3 className="text-lg font-bold text-khiimori-text-primary mb-3">Pro Tips</h3>
              <ul className="space-y-2 text-sm text-khiimori-text-secondary">
                <li>• Use multiple providers for redundancy</li>
                <li>• Set priorities to control provider selection</li>
                <li>• Monitor costs to stay within budget</li>
                <li>• Test connections after configuration</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
